import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Star, EnvelopeSimple, Copy, Sparkle, Trash, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ customer_name: "", rating: 5, text: "", service: "" });
  const [reqForm, setReqForm] = useState({ customer_name: "", service_done: "" });
  const [emailResult, setEmailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/reviews").then(r => setReviews(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.customer_name || !form.text) return toast.error("Name and text required");
    await api.post("/reviews", { ...form, rating: Number(form.rating) });
    toast.success("Review saved"); setForm({ customer_name: "", rating: 5, text: "", service: "" }); load();
  };
  const removeRev = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    await api.delete(`/reviews/${id}`); toast.success("Review deleted"); load();
  };
  const togglePublish = async (r) => {
    const next = !(r.published !== false);
    await api.patch(`/reviews/${r.id}`, { published: next });
    toast.success(next ? "Review published" : "Review hidden"); load();
  };
  const genEmail = async () => {
    if (!reqForm.customer_name) return toast.error("Customer name required");
    setLoading(true);
    try { const r = await api.post("/reviews/request", reqForm); setEmailResult(r.data); }
    catch { toast.error("Failed"); } finally { setLoading(false); }
  };
  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : 0;
  const pendingCount = reviews.filter(r => r.published === false).length;

  return (
    <div data-testid="reviews-page">
      <PageHeader eyebrow="Social Proof" title="Reviews" description="Manage customer reviews. Public submissions appear here pending approval." />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Card className="sharp-card p-5 bg-white"><div className="eyebrow eyebrow-dark">Total reviews</div><div className="font-display text-4xl tracking-wide text-navy mt-2">{reviews.length}</div></Card>
        <Card className="sharp-card p-5 bg-white"><div className="eyebrow eyebrow-dark">Avg rating</div><div className="font-display text-4xl tracking-wide text-navy mt-2 flex items-center gap-2">{avg}<Star size={22} weight="fill" className="text-navy" /></div></Card>
        <Card className="sharp-card p-5 bg-white"><div className="eyebrow eyebrow-dark">5★ reviews</div><div className="font-display text-4xl tracking-wide text-navy mt-2">{reviews.filter(r=>r.rating===5).length}</div></Card>
        <Card className="sharp-card p-5 bg-white"><div className="eyebrow eyebrow-dark">Pending approval</div><div className="font-display text-4xl tracking-wide text-navy mt-2">{pendingCount}</div></Card>
      </div>

      <Tabs defaultValue="list" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-sm p-1 h-auto">
          <TabsTrigger value="list" className="rounded-sm px-4 py-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white">All reviews{pendingCount > 0 && <Badge className="ml-2 bg-navy text-white border-0 rounded-sm">{pendingCount}</Badge>}</TabsTrigger>
          <TabsTrigger value="add" className="rounded-sm px-4 py-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white">Add review</TabsTrigger>
          <TabsTrigger value="email" className="rounded-sm px-4 py-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white">Request email</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-6">
          {reviews.length === 0 ? <Card className="sharp-card p-10 text-center text-sm text-slate-500">No reviews yet</Card> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {reviews.map(r => {
                const published = r.published !== false;
                return (
                  <Card key={r.id} data-testid={`adm-rev-${r.id}`} className={`sharp-card p-5 ${published ? "bg-white" : "border-slate-300 bg-slate-50/30"}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-medium text-navy truncate min-w-0 flex-1">{r.customer_name}</div>
                      <div className="flex items-center gap-1 flex-wrap shrink-0">
                        {!published && <Badge variant="outline" className="rounded-sm border-slate-300 text-slate-700 bg-slate-50 text-[10px]">Pending</Badge>}
                        {r.source === "google" && <Badge variant="outline" className="rounded-sm text-[10px] border-red-200 text-red-700 bg-red-50">Google</Badge>}
                        {r.source !== "google" && r.submitted_via === "website" && <Badge variant="outline" className="rounded-sm text-[10px]">Public</Badge>}
                      </div>
                    </div>
                    <div className="flex gap-0.5 mt-1">{Array.from({length: 5}).map((_, i) => <Star key={`s-${i}`} size={12} weight={i < r.rating ? "fill" : "regular"} className={i < r.rating ? "text-navy" : "text-slate-300"} />)}</div>
                    {r.service && <div className="text-xs text-slate-500 mt-1">{r.service}</div>}
                    <p className="mt-2 text-sm text-slate-700">{r.text}</p>
                    <div className="mt-3 flex gap-2 pt-2 border-t border-slate-100">
                      <Button data-testid={`rev-toggle-${r.id}`} variant="outline" size="sm" className="rounded-sm" onClick={()=>togglePublish(r)}>{published ? <><EyeSlash size={12} className="mr-1"/>Hide</> : <><Eye size={12} className="mr-1"/>Publish</>}</Button>
                      <Button data-testid={`rev-del-${r.id}`} variant="outline" size="sm" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" onClick={()=>removeRev(r.id)}><Trash size={12} className="mr-1"/>Delete</Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="add" className="mt-6">
          <Card className="sharp-card p-6 max-w-2xl bg-white">
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Customer name</Label><Input data-testid="rev-name" value={form.customer_name} onChange={(e)=>setForm({...form, customer_name: e.target.value})} className="rounded-sm mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Service</Label><Input data-testid="rev-service" value={form.service} onChange={(e)=>setForm({...form, service: e.target.value})} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Rating</Label><Input data-testid="rev-rating" type="number" min={1} max={5} value={form.rating} onChange={(e)=>setForm({...form, rating: e.target.value})} className="rounded-sm mt-1" /></div>
              </div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Review text</Label><Textarea data-testid="rev-text" rows={4} value={form.text} onChange={(e)=>setForm({...form, text: e.target.value})} className="rounded-sm mt-1" /></div>
              <Button data-testid="btn-submit-rev" onClick={submit} className="pill-navy">Save review</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="email" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="sharp-card p-6 bg-white">
              <div className="space-y-3">
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Customer name</Label><Input data-testid="rev-req-name" value={reqForm.customer_name} onChange={(e)=>setReqForm({...reqForm, customer_name: e.target.value})} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Service done</Label><Input data-testid="rev-req-service" value={reqForm.service_done} onChange={(e)=>setReqForm({...reqForm, service_done: e.target.value})} placeholder="e.g. fixed kitchen sink leak" className="rounded-sm mt-1" /></div>
                <Button data-testid="btn-gen-req-email" onClick={genEmail} disabled={loading} className="w-full pill-navy justify-center !py-3"><Sparkle size={16} className="mr-2" />{loading ? "Writing..." : "Write request email"}</Button>
              </div>
            </Card>
            <Card className="sharp-card p-6 min-h-[300px] bg-white">
              {!emailResult ? <div className="text-slate-400 text-sm h-full flex items-center justify-center"><EnvelopeSimple size={24} className="mr-2" />Generated email will appear here</div> : (
                <div>
                  <div className="flex items-center justify-between mb-2"><div className="eyebrow eyebrow-dark">Subject</div><Button size="sm" variant="outline" className="rounded-sm" onClick={()=>{navigator.clipboard.writeText(emailResult.body); toast.success("Body copied");}}><Copy size={12} className="mr-1" />Copy body</Button></div>
                  <div className="font-semibold text-navy mb-3">{emailResult.subject}</div>
                  <div className="eyebrow eyebrow-dark mb-1">Body</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{emailResult.body}</p>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
